import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, BookOpen, Sparkles } from 'lucide-react';

export default function Blog() {
  const posts = [
    {
      slug: '/blog/polymarket-ultimate-guide',
      title: 'The Ultimate Polymarket Guide: How It Works, How to Trade, and How to Copy Traders Safely',
      tag: 'Ultimate guide',
      estRead: '25–35 min read',
      description:
        'A complete Polymarket education: YES/NO mechanics, the order book, liquidity & slippage, strategy archetypes, risk management, copy trading, paper trading, and resolution/redeem.',
    },
    {
      slug: '/blog/polymarket-copy-trading',
      title: 'Turn Elite Polymarket Traders Into Your Personal Signal Feed With BetMimic',
      tag: 'Copy Trading',
      estRead: '12 min read',
      description:
        'A practical breakdown of Polymarket copy trading, how BetMimic executes on the official CLOB, and how to get your first copied trade live with real PnL tracking.',
    },
    {
      slug: '/blog/polymarket-risk-management',
      title: 'Risk Management for Polymarket Copy Trading',
      tag: 'Risk & sizing',
      estRead: '22 min read',
      description:
        'Build a bankroll plan, configure per-config and per-trade limits, and avoid common blow-up patterns when copying traders on Polymarket.',
    },
    {
      slug: '/blog/polymarket-copy-trading-strategies',
      title: 'Polymarket Copy Trading Strategies that Actually Scale',
      tag: 'Strategy',
      estRead: '20 min read',
      description:
        'Three concrete BetMimic setups you can run today: conservative bankroll copier, aggressive degen wing, and diversified copy trading portfolio.',
    },
    {
      slug: '/blog/betmimic-vs-manual-polymarket',
      title: 'BetMimic vs Manual Polymarket Trading',
      tag: 'Playbook',
      estRead: '16 min read',
      description:
        'Compare fully manual Polymarket trading against BetMimic automation and see when to lean on copy trading vs your own conviction.',
    },
    {
      slug: '/blog/polymarket-copy-trading-playbook',
      title: 'The Ultimate Polymarket Copy Trading Playbook with BetMimic',
      tag: 'Deep dive',
      estRead: '30+ min read',
      description:
        'From first USDC.e deposit to running multiple copy configs with professional-style risk rules – a complete operating manual for BetMimic.',
    },
    {
      slug: '/blog/polymarket-degen-copy-trading',
      title: 'Polymarket Degen Copy Trading with BetMimic (For Real Degens)',
      tag: 'Degens',
      estRead: '14 min read',
      description:
        'A straight-shot guide for Polymarket degenerates who want a real copy trading bot tied to the official CLOB, not screenshots and vibes.',
    },
    {
      slug: '/blog/polymarket-sports-betting-degens',
      title: 'Polymarket Sports Betting Degens: Copy Winners Instead of Chasing Live Lines',
      tag: 'Sports',
      estRead: '15 min read',
      description:
        'How Polymarket sports betting degens can use BetMimic to copy sharp wallets, control tilt, and keep USDC.e bankrolls alive through NFL, NBA and soccer seasons.',
    },
    {
      slug: '/blog/polymarket-trading-tool',
      title: 'The Complete Polymarket Trading Tool Guide (Built Around BetMimic)',
      tag: 'Tools',
      estRead: '25+ min read',
      description:
        'A pillar guide to Polymarket trading tools: what serious traders need, how BetMimic sits on top of Polymarket as a non-custodial trading layer, and how to turn your wallet into a real dashboard.',
    },
    {
      slug: '/blog/polymarket-tools',
      title: 'The Best Polymarket Tools (2026): Analytics, Bots, Copy Trading, APIs, and Pro Workflows',
      tag: 'Tools',
      estRead: '35–50 min read',
      description:
        'A deep guide to Polymarket tools: analytics dashboards, order book execution, portfolio tracking, alerts, trading bots, and copy trading systems built on the official CLOB.',
    },
    {
      slug: '/blog/unlocking-copy-trading-polymarket',
      title: 'Unlocking the Power of Copy Trading on Polymarket with BetMimic',
      tag: 'Copy Trading',
      estRead: '10–14 min read',
      description:
        'A step-by-step guide to copy trading on Polymarket with BetMimic: how it works, key features, and practical risk rules for beginners through degens.',
    },
    {
      slug: '/blog/polymarket-trade-alerts',
      title: 'Polymarket Trade Alerts: How to Get Wallet Alerts (Discord/Telegram) + What “Real” Alerts Should Track',
      tag: 'Alerts',
      estRead: '12–16 min read',
      description:
        'A practical guide to Polymarket trade alerts: wallet alerts, price alerts, liquidity alerts, and how to build a clean Discord/Telegram workflow that doesn’t spam you.',
    },
    {
      slug: '/blog/polymarket-paper-trading',
      title: 'Paper Trading on Polymarket: How to Stress-Test a Strategy Without Burning Real USDC.e',
      tag: 'Paper',
      estRead: '14 min read',
      description:
        'A structured paper trading workflow for Polymarket: what to measure, what to ignore, and how to run paper like a real risk lab instead of fantasy PnL.',
    },
    {
      slug: '/blog/polymarket-exit-discipline',
      title: 'Exit Discipline on Polymarket: Stop Loss, Take Profit, and the "Don’t Get Chopped" Playbook',
      tag: 'Risk & exits',
      estRead: '12 min read',
      description:
        'A practical exit framework for Polymarket: stop-loss and take-profit rules, chop reduction, and how to apply it cleanly (manually or with BetMimic).',
    },
    {
      slug: '/blog/polymarket-choosing-traders',
      title: 'How to Choose Polymarket Traders to Copy (Without Getting Farmed)',
      tag: 'Trader selection',
      estRead: '10 min read',
      description:
        'A practical checklist for selecting copy traders: avoiding one-week wonders, reading trade behavior, and setting safe config limits.',
    },
    {
      slug: '/blog/polymarket-resolution-redeem',
      title: 'Market Resolved on Polymarket? How Redeem Works (and Why Positions Sometimes Look Weird)',
      tag: 'Settlement',
      estRead: '9 min read',
      description:
        'Understand resolved vs redeemable vs redeemed, why price endpoints can 404 after settlement, and what to do when a position looks “stuck”.',
    },
  ];

  return (
    <div className="min-h-screen bg-dark-900 text-dark-100">
      <div className="max-w-5xl mx-auto px-4 py-10 md:py-14">
        <header className="mb-10 md:mb-12">
          <div className="flex items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary-600/70 to-accent-500/70 flex items-center justify-center shadow-lg shadow-primary-600/30">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-primary-400 flex items-center gap-1">
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-400" />
                  Polymarket copy trading blog
                </p>
                <h1 className="text-2xl md:text-3xl font-bold mt-1">
                  Learn Polymarket & BetMimic, step-by-step
                </h1>
              </div>
            </div>
            <Link
              to="/login"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs text-dark-400 hover:text-primary-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>

          <p className="text-sm md:text-base text-dark-300 max-w-3xl">
            This is the education hub for BetMimic users who trade on{' '}
            <span className="text-primary-300 font-medium">Polymarket</span>. Every article is written to be
            actionable: wallet setup, copy trading configs, risk frameworks, and day-to-day operating habits that
            keep you in the game.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-dark-500">
            <span className="inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-primary-400" />
              Updated as BetMimic evolves
            </span>
            <span className="hidden sm:inline">·</span>
            <span>Beginner-friendly, but deep enough for grinders.</span>
          </div>
        </header>

        <main className="space-y-8">
          <section>
            <h2 className="text-sm font-semibold text-dark-200 mb-3 uppercase tracking-[0.2em]">
              Featured guides
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {posts.map((post) => (
                <article
                  key={post.slug}
                  className="group rounded-2xl border border-dark-700 bg-dark-800/80 hover:border-primary-500/60 hover:bg-dark-800 transition-colors p-5 flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-dark-500 mb-1">
                      <span className="inline-flex items-center gap-1 rounded-full border border-dark-600/80 px-2 py-0.5 bg-dark-900/40">
                        <span className="h-1.5 w-1.5 rounded-full bg-primary-400" />
                        {post.tag}
                      </span>
                      <span>{post.estRead}</span>
                    </div>
                    <h3 className="text-base md:text-lg font-semibold group-hover:text-primary-200 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-xs md:text-sm text-dark-300 leading-relaxed">
                      {post.description}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <Link
                      to={post.slug}
                      className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-200 font-medium"
                    >
                      Read article
                      <ExternalLink className="w-3 h-3" />
                    </Link>
                    <span className="text-dark-500 text-[0.7rem]">Focused on Polymarket & BetMimic</span>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-dark-700 bg-dark-800/70 p-5 md:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-sm md:text-base font-semibold mb-1 flex items-center gap-2">
                Want direct help with your setup?
                <Sparkles className="w-4 h-4 text-primary-400" />
              </h2>
              <p className="text-xs md:text-sm text-dark-300 max-w-xl">
                If you get stuck wiring your wallet, configuring copy limits, or reconciling PnL with Polymarket, join
                the BetMimic Discord and ask in the support channel.
              </p>
            </div>
            <a
              href="https://discord.gg/dFSefc9Hs9"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-primary-600/90 hover:bg-primary-500 text-sm font-medium text-white shadow-lg shadow-primary-600/40"
            >
              Join Discord support
            </a>
          </section>
        </main>
      </div>
    </div>
  );
}
